<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/PageHeader.vue'
import SearchBox from '@/components/SearchBox.vue'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import { useTags } from '@/composables/useTags'
import { useTodosStore } from '@/stores/todos'
import { collectCategories, searchCategories } from '@/todos/categories'
import { chipStyle } from '@/todos/tags'
import UiRow from '@/components/UiRow.vue'

// The Categories tab: every tag used on a todo of any subscribed list, so a tag page is one tap away. The todos are read live
// like on the list pages (no extra cache); a tag that is only on one list links straight to that list's tag page, one on
// several lists unfolds into a row per list. `?q=` keeps the search in the URL (replaced, so it makes no history entries).
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useTodosStore()
const { tags, colorFor } = useTags()

const query = ref(typeof route.query.q === 'string' ? route.query.q : '')
const opened = ref(new Set<string>())
const loading = ref(false)
const failed = ref(false)

watch(query, (q) => {
  const rest = { ...route.query }
  delete rest.q
  void router.replace({ query: q ? { ...rest, q } : rest })
})

// (the built-in tags, like the subtask one, are no categories to browse)
const hidden = computed(
  () => new Set(tags.value.filter((tag) => tag.internal).map((tag) => tag.name.toLowerCase())),
)
const categories = computed(() =>
  collectCategories(store.todosByCalendar, store.calendars, hidden.value),
)
const shown = computed(() => searchCategories(categories.value, query.value))
const errors = computed(() => store.calendars.filter((c) => store.errors[c.id]).map((c) => c.name))

function tagPath(calendarId: number, name: string): string {
  return `/todos/${calendarId}/tag/${encodeURIComponent(name)}`
}

function toggle(name: string): void {
  const next = new Set(opened.value)
  if (!next.delete(name)) next.add(name)
  opened.value = next
}

onMounted(async () => {
  loading.value = true
  try {
    await store.loadCalendars()
    await Promise.all(store.calendars.map((calendar) => store.loadTodos(calendar.id)))
  } catch {
    failed.value = true
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <section class="categories-page">
    <PageHeader :title="t('categories.title')" :hint="t('categories.hint')" />

    <UiGroup>
      <SearchBox
        v-model="query"
        :placeholder="t('list.search')"
        :clear-label="t('list.clearSearch')"
      />
      <template #footer>
        <p v-if="failed" class="ui-error">{{ t('categories.loadFailed') }}</p>
        <p v-else-if="errors.length" class="ui-error">
          {{ t('categories.listsFailed', { lists: errors.join(', ') }) }}
        </p>
        <p v-if="loading && !categories.length">{{ t('common.loading') }}</p>
        <p v-else-if="!categories.length && !failed">{{ t('categories.empty') }}</p>
        <p v-else-if="query && !shown.length">{{ t('categories.noMatches') }}</p>
      </template>
    </UiGroup>

    <UiGroup v-if="shown.length">
      <UiList>
        <template v-for="category in shown" :key="category.name.toLowerCase()">
          <UiRow
            v-if="category.calendars.length === 1"
            :to="tagPath(category.calendars[0].id, category.name)"
            class="entry"
            data-category
          >
            <span class="label" :style="chipStyle(colorFor(category.name))"
              ># {{ category.name }}</span
            >
            <span class="spacer"></span>
            <span class="ui-hint">{{ category.total }}</span>
            <span class="go">›</span>
          </UiRow>
          <template v-else>
            <UiRow
              as="button"
              type="button"
              class="entry"
              data-category
              :aria-expanded="opened.has(category.name)"
              @click="toggle(category.name)"
            >
              <span class="label" :style="chipStyle(colorFor(category.name))"
                ># {{ category.name }}</span
              >
              <span class="spacer"></span>
              <span class="ui-hint">{{ t('categories.inLists', category.calendars.length) }}</span>
              <span class="ui-hint">{{ category.total }}</span>
              <span class="go">{{ opened.has(category.name) ? '▾' : '▸' }}</span>
            </UiRow>
            <template v-if="opened.has(category.name)">
              <UiRow
                v-for="calendar in category.calendars"
                :key="calendar.id"
                :to="tagPath(calendar.id, category.name)"
                class="entry nested"
                data-category-calendar
              >
                <span class="name">{{ calendar.name }}</span>
                <span class="spacer"></span>
                <span class="ui-hint">{{ calendar.count }}</span>
                <span class="go">›</span>
              </UiRow>
            </template>
          </template>
        </template>
      </UiList>
    </UiGroup>
  </section>
</template>

<style scoped>
.entry {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  text-decoration: none;
  color: inherit;
  font: inherit;
}

.label {
  min-width: 0;
  overflow-wrap: anywhere;
  border-radius: 0.5rem;
  padding: 0.1rem 0.4rem;
}

.name {
  min-width: 0;
  overflow-wrap: anywhere;
}

.nested {
  padding-left: 1.5rem;
}

.spacer {
  flex: 1;
}

.go {
  color: var(--tg-hint-color);
}
</style>
